import { ConfirmationAction } from '@app/components/ConfirmationAction';
import { PermissionsCatalogResponseDto, type PermissionDefinitionDto } from '@mi-front/api-clients';
import { NullState, ViewLoader } from '@mi-front/ui';
import RotateLeftIcon from '@mui/icons-material/RotateLeft';
import { Box, Button } from '@mui/material';
import { useMemo } from 'react';
import { useIntl } from 'react-intl';
import { ActionPermissionsSection } from './ActionPermissionsSection';
// Generic interface for permission item components
export interface PermissionItemComponentProps<T> {
  readonly permissionState: T;
  readonly permissionDefinition: PermissionDefinitionDto;
}

// Generic interface for the view props
export interface ActionPermissionsViewProps<T> {
  readonly catalog?: PermissionsCatalogResponseDto;
  readonly permissions?: T[];
  readonly PermissionItemComponent: React.ComponentType<PermissionItemComponentProps<T>>;
  readonly isLoading?: boolean;
  readonly error?: Error | null;
  readonly onReset?: () => Promise<void>;
}

export function ActionPermissionsView<T>(p: ActionPermissionsViewProps<T>) {
  const { $t } = useIntl();
  const {
    catalog,
    permissions,
    PermissionItemComponent,
    isLoading = false,
    error = null,
    onReset,
  } = p;

  // Create a map of existing permissions for quick lookup
  const existingPermissionsMap = useMemo(() => {
    return new Map<string, T>(permissions?.map((p: any) => [p.permission, p]) || []);
  }, [permissions]);

  // Loading state
  if (isLoading) {
    return <ViewLoader />;
  }

  // Error state
  if (error) {
    return (
      <NullState>
        <div className="text-center">
          <h5>
            {$t({
              id: 'permissions.error.title',
              defaultMessage: 'Error al cargar permisos',
            })}
          </h5>
          <p>
            {$t({
              id: 'permissions.error.description',
              defaultMessage:
                'Hubo un error al cargar los permisos. Por favor, inténtalo de nuevo.',
            })}
          </p>
        </div>
      </NullState>
    );
  }

  // Empty state
  if (!catalog || catalog.permissionsByProvider.length === 0) {
    return (
      <NullState>
        <div className="text-center">
          <h5>
            {$t({
              id: 'permissions.empty.title',
              defaultMessage: 'No se encontraron permisos',
            })}
          </h5>
          <p>
            {$t({
              id: 'permissions.empty.description',
              defaultMessage: 'No hay permisos disponibles.',
            })}
          </p>
        </div>
      </NullState>
    );
  }

  return (
    <div>
      {catalog.permissionsByProvider.map((providerData) => {
        // Get permissions for this provider
        const providerPermissions = providerData.permissions.map((permission) => {
          const existing = existingPermissionsMap.get(permission.id);

          if (existing) {
            return existing;
          }

          // Create a default permission state for unassigned permissions
          return {
            permission: permission.id,
            allowed: false,
          } as T;
        });

        return (
          <ActionPermissionsSection<T>
            key={providerData.provider.id}
            provider={providerData.provider}
            permissions={providerData.permissions}
            permissionsState={providerPermissions}
            PermissionItemComponent={PermissionItemComponent}
          />
        );
      })}

      {onReset && (
        <Box mt={4}>
          <ConfirmationAction
            message={$t({
              id: 'admin.permissions.resetPermissionsPrompt',
              defaultMessage:
                '¿Estás seguro de que quieres restablecer todos los permisos? Esta acción no se puede deshacer.',
            })}
            confirmLabel={$t({
              id: 'admin.permissions.reset',
              defaultMessage: 'Restablecer',
            })}
            cancelLabel={$t({
              id: 'common.cancel',
              defaultMessage: 'Cancelar',
            })}
            confirmColor="warning"
            onConfirm={() => onReset()}
          >
            {({ onClick }) => (
              <Button
                variant="outlined"
                onClick={onClick}
                color="warning"
                startIcon={<RotateLeftIcon />}
              >
                {$t({
                  id: 'admin.permissions.resetPermissions',
                  defaultMessage: 'Restablecer permisos',
                })}
              </Button>
            )}
          </ConfirmationAction>
        </Box>
      )}
    </div>
  );
}
